import jsPDF from 'jspdf';
import type { Wine } from '../types';
import {
  JAPANESE_FONT_FAMILY,
  loadJapaneseFontBase64,
  registerJapaneseFont,
} from './loadJapaneseFont';
import { getCachedImageDataUrl, getImageFormat, preloadWineImages } from './pdfImageLoader';

export interface WineListPdfLayout {
  headerHeight: number;
  rowMinHeight: number;
  genreSectionSpacer: number;
  genreTitleFontSize: number;
  genreTitleHeight: number;
  lineHeightExtra: number;
  rowPadding: number;
  imageColWidth: number;
  typeColWidth: number;
  imageSize: number;
  cellInset: number;
  headerTextY: number;
  cellTextY: number;
  genreEndPadding: number;
  dateFontSize: number;
  showDate: boolean;
  showPageNumbers: boolean;
  preventPageBreak: boolean;
  titleOverride?: string;
  subtitleFontSize: number;
  showShopAsSubtitle: boolean;
  /** タイトル左・日付右の1行ヘッダー（通常リスト用） */
  inlineHeader: boolean;
  /**
   * ページ分割（通常リスト: 1ページ目=泡/ロゼ/白、2ページ目=赤）
   * 未指定時は全ジャンルを1まとまりとして扱う
   */
  pageGroups?: Wine['type'][][];
  /** 画像を縦基準でクロップして正方形表示 */
  centerCropImages: boolean;
  /**
   * 詳細を2行表示:
   * 1行目: ワイン名 …… ボトル価格
   * 2行目: 品種：…　産地：… …… (G¥…)
   */
  stackedDetail: boolean;
  glassPriceOnly: boolean;
  distributeVertically: boolean;
}

interface VerticalDistribution {
  verticalTopOffset: number;
  uniformRowHeight: number;
  genreSectionSpacer: number;
  genreEndPadding: number;
  titleMarginBottom: number;
  imageSize: number;
  tableFontSize: number;
  genreTitleFontSize: number;
  genreTitleHeight: number;
  headerHeight: number;
  headerTextY: number;
  imageColWidth: number;
  typeColWidth: number;
  cellInset: number;
  titleFontSize: number;
}

export interface WineListPdfStyle {
  pageSize: 'a4' | 'letter';
  orientation: 'portrait' | 'landscape';
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  title: {
    fontSize: number;
    color: [number, number, number];
    marginBottom: number;
  };
  table: {
    headerBackgroundColor: [number, number, number];
    headerTextColor: [number, number, number];
    rowBackgroundColor: [number, number, number];
    alternateRowBackgroundColor: [number, number, number];
    textColor: [number, number, number];
    fontSize: number;
    cellPadding: number;
    lineWidth: number;
  };
  winePerPage: number;
  showImage: boolean;
  showCharacteristics: boolean;
  showGlassPrice: boolean;
  layout: WineListPdfLayout;
}

const defaultLayout: WineListPdfLayout = {
  headerHeight: 16,
  rowMinHeight: 34,
  genreSectionSpacer: 10,
  genreTitleFontSize: 11,
  genreTitleHeight: 13,
  lineHeightExtra: 2,
  rowPadding: 6,
  imageColWidth: 40,
  typeColWidth: 24,
  imageSize: 28,
  cellInset: 6,
  headerTextY: 11,
  cellTextY: 10,
  genreEndPadding: 4,
  dateFontSize: 8,
  showDate: true,
  showPageNumbers: true,
  preventPageBreak: false,
  titleOverride: 'WINE LIST',
  subtitleFontSize: 10,
  showShopAsSubtitle: false,
  inlineHeader: true,
  pageGroups: [
    ['スパークリング', 'ロゼ', '白'],
    ['赤'],
  ],
  centerCropImages: true,
  stackedDetail: true,
  glassPriceOnly: false,
  distributeVertically: true,
};

export const defaultPdfStyle: WineListPdfStyle = {
  pageSize: 'a4',
  orientation: 'portrait',
  margin: {
    top: 24,
    right: 28,
    bottom: 28,
    left: 28,
  },
  title: {
    fontSize: 13,
    color: [114, 47, 55],
    marginBottom: 8,
  },
  table: {
    headerBackgroundColor: [114, 47, 55],
    headerTextColor: [255, 255, 255],
    rowBackgroundColor: [255, 255, 255],
    alternateRowBackgroundColor: [250, 250, 250],
    textColor: [0, 0, 0],
    fontSize: 8,
    cellPadding: 3,
    lineWidth: 0.1,
  },
  winePerPage: 0,
  showImage: true,
  showCharacteristics: false,
  showGlassPrice: true,
  layout: defaultLayout,
};

/** グラスワインリスト用（1ページに収めるコンパクト設定） */
export const glassPdfStyle: Partial<WineListPdfStyle> = {
  margin: {
    top: 22,
    right: 26,
    bottom: 22,
    left: 26,
  },
  title: {
    fontSize: 13,
    color: [114, 47, 55],
    marginBottom: 2,
  },
  table: {
    headerBackgroundColor: [114, 47, 55],
    headerTextColor: [255, 255, 255],
    rowBackgroundColor: [255, 255, 255],
    alternateRowBackgroundColor: [248, 248, 248],
    textColor: [0, 0, 0],
    fontSize: 7,
    cellPadding: 2,
    lineWidth: 0.05,
  },
  showImage: true,
  showCharacteristics: false,
  showGlassPrice: true,
  layout: {
    ...defaultLayout,
    headerHeight: 13,
    rowMinHeight: 28,
    genreSectionSpacer: 8,
    genreTitleFontSize: 9,
    genreTitleHeight: 11,
    lineHeightExtra: 1.5,
    rowPadding: 5,
    imageColWidth: 28,
    typeColWidth: 20,
    imageSize: 18,
    cellInset: 6,
    headerTextY: 9,
    cellTextY: 8,
    genreEndPadding: 4,
    dateFontSize: 7,
    showDate: false,
    showPageNumbers: false,
    preventPageBreak: true,
    titleOverride: "TODAY'S GLASS",
    subtitleFontSize: 7,
    showShopAsSubtitle: true,
    inlineHeader: false,
    pageGroups: undefined,
    centerCropImages: true,
    stackedDetail: true,
    glassPriceOnly: true,
    distributeVertically: true,
  },
};

const typeShortLabel: Record<Wine['type'], string> = {
  赤: '赤',
  白: '白',
  ロゼ: 'ロゼ',
  スパークリング: '泡',
};

const setPdfFont = (doc: jsPDF, size: number) => {
  doc.setFont(JAPANESE_FONT_FAMILY, 'normal');
  doc.setFontSize(size);
};

const getColumnLayout = (
  tableWidth: number,
  showImage: boolean,
  showCharacteristics: boolean,
  imageColWidth: number,
  typeColWidth: number,
  stackedDetail: boolean
) => {
  const headers: string[] = [];
  const widths: number[] = [];

  if (showImage) {
    headers.push('画像');
    widths.push(imageColWidth);
  }
  headers.push('種類');
  widths.push(typeColWidth);

  const fixedWidth = widths.reduce((sum, width) => sum + width, 0);
  const flexWidth = tableWidth - fixedWidth;

  if (stackedDetail) {
    headers.push('ワイン');
    widths.push(flexWidth);
    return { headers, widths };
  }

  if (showCharacteristics) {
    headers.push('ワイン名', '品種', '産地', '特徴', '価格');
    const ratios = [0.24, 0.16, 0.16, 0.22, 0.22];
    ratios.forEach((ratio) => widths.push(flexWidth * ratio));
  } else {
    headers.push('ワイン名', '品種', '産地', '価格');
    const ratios = [0.30, 0.22, 0.22, 0.26];
    ratios.forEach((ratio) => widths.push(flexWidth * ratio));
  }

  return { headers, widths };
};

/** 品種・産地用の明るめワインレッド */
const META_TEXT_COLOR: [number, number, number] = [168, 96, 110];

const truncateToWidth = (doc: jsPDF, text: string, maxWidth: number): string => {
  if (!text) return '';
  if (doc.getTextWidth(text) <= maxWidth) return text;
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = `${text.slice(0, mid)}${ellipsis}`;
    if (doc.getTextWidth(candidate) <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo > 0 ? `${text.slice(0, lo)}${ellipsis}` : ellipsis;
};

const scaleValue = (value: number, scale: number, min: number) =>
  Math.max(min, value * scale);

/**
 * ページ内の本数に合わせて行高・文字サイズを自動調整し、必ず1ページに収める。
 * 本数が少ないときは行を伸ばして余白を埋める。
 */
const computeVerticalDistribution = (
  pageHeight: number,
  pdfStyle: WineListPdfStyle,
  baseLayout: WineListPdfLayout,
  winesByType: Record<Wine['type'], Wine[]>,
  typeOrder: Wine['type'][],
  shopName?: string
): VerticalDistribution => {
  const activeGenres = typeOrder.filter((type) => winesByType[type].length > 0);
  const totalWineCount = activeGenres.reduce(
    (sum, type) => sum + winesByType[type].length,
    0
  );
  const genreCount = activeGenres.length;
  const usableHeight = pageHeight - pdfStyle.margin.top - pdfStyle.margin.bottom;

  const absoluteMinRow = baseLayout.stackedDetail ? 20 : 14;
  const maxRow = baseLayout.stackedDetail ? 58 : 50;

  const evaluate = (scale: number) => {
    const tableFontSize = scaleValue(pdfStyle.table.fontSize, scale, 5.5);
    const titleFontSize = scaleValue(pdfStyle.title.fontSize, Math.max(scale, 0.7), 9);
    const titleMarginBottom = scaleValue(pdfStyle.title.marginBottom, scale, 2);
    const genreTitleFontSize = scaleValue(baseLayout.genreTitleFontSize, scale, 6.5);
    const genreTitleHeight = scaleValue(baseLayout.genreTitleHeight, scale, 8);
    const headerHeight = scaleValue(baseLayout.headerHeight, scale, 9);
    const headerTextY = scaleValue(baseLayout.headerTextY, scale, 6);
    const genreSectionSpacer = scaleValue(baseLayout.genreSectionSpacer, scale, 2);
    const genreEndPadding = scaleValue(baseLayout.genreEndPadding, scale, 1);
    const imageColWidth = scaleValue(baseLayout.imageColWidth, scale, 22);
    const typeColWidth = scaleValue(baseLayout.typeColWidth, scale, 16);
    const cellInset = scaleValue(baseLayout.cellInset, scale, 3);
    const baseImageSize = scaleValue(baseLayout.imageSize, scale, 12);

    const titleBlockHeight = baseLayout.inlineHeader
      ? titleFontSize + titleMarginBottom
      : titleFontSize + 2 +
        (baseLayout.showShopAsSubtitle && shopName
          ? scaleValue(baseLayout.subtitleFontSize, scale, 5) + 2
          : 0) +
        titleMarginBottom +
        (baseLayout.showDate ? scaleValue(baseLayout.dateFontSize, scale, 5) + 6 : 0);

    const structureHeight =
      genreCount * (genreTitleHeight + headerHeight + genreEndPadding) +
      Math.max(0, genreCount - 1) * genreSectionSpacer;

    const rowBudget = Math.max(0, usableHeight - titleBlockHeight - structureHeight);
    const rawRowHeight = totalWineCount > 0 ? rowBudget / totalWineCount : maxRow;

    return {
      tableFontSize,
      titleFontSize,
      titleMarginBottom,
      genreTitleFontSize,
      genreTitleHeight,
      headerHeight,
      headerTextY,
      genreSectionSpacer,
      genreEndPadding,
      imageColWidth,
      typeColWidth,
      cellInset,
      baseImageSize,
      titleBlockHeight,
      structureHeight,
      rawRowHeight,
      fits: rawRowHeight >= absoluteMinRow - 0.01,
    };
  };

  let chosen = evaluate(1);
  let chosenScale = 1;

  if (!chosen.fits) {
    let lo = 0.5;
    let hi = 1;
    for (let i = 0; i < 18; i++) {
      const mid = (lo + hi) / 2;
      const candidate = evaluate(mid);
      if (candidate.fits) {
        chosen = candidate;
        chosenScale = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }
    if (!chosen.fits) {
      chosenScale = 0.5;
      chosen = evaluate(chosenScale);
    }
  }

  let uniformRowHeight = Math.min(maxRow, Math.max(absoluteMinRow * 0.9, chosen.rawRowHeight));
  const maxFittingRow =
    totalWineCount > 0
      ? Math.max(
          absoluteMinRow * 0.85,
          (usableHeight - chosen.titleBlockHeight - chosen.structureHeight) / totalWineCount
        )
      : maxRow;
  uniformRowHeight = Math.min(uniformRowHeight, maxFittingRow);

  const contentHeight =
    chosen.titleBlockHeight +
    chosen.structureHeight +
    uniformRowHeight * totalWineCount;
  const slack = Math.max(0, usableHeight - contentHeight);

  const verticalTopOffset = slack * 0.1;
  const spacerExtra =
    genreCount > 1 ? (slack * 0.5) / (genreCount - 1) : slack * 0.2;
  const endPadExtra = genreCount > 0 ? (slack * 0.25) / genreCount : 0;
  const titleMarginExtra = slack * 0.15;

  const imageSize = Math.min(
    uniformRowHeight - 4,
    Math.max(chosen.baseImageSize, Math.min(chosen.imageColWidth - 4, uniformRowHeight - 4))
  );

  return {
    verticalTopOffset,
    uniformRowHeight,
    genreSectionSpacer: chosen.genreSectionSpacer + spacerExtra,
    genreEndPadding: chosen.genreEndPadding + endPadExtra,
    titleMarginBottom: chosen.titleMarginBottom + titleMarginExtra,
    imageSize: Math.max(11, imageSize),
    tableFontSize: chosen.tableFontSize,
    genreTitleFontSize: chosen.genreTitleFontSize,
    genreTitleHeight: chosen.genreTitleHeight,
    headerHeight: chosen.headerHeight,
    headerTextY: Math.min(chosen.headerTextY, chosen.headerHeight - 2),
    imageColWidth: chosen.imageColWidth,
    typeColWidth: chosen.typeColWidth,
    cellInset: chosen.cellInset,
    titleFontSize: chosen.titleFontSize,
  };
};

const mergePdfStyle = (style: Partial<WineListPdfStyle> = {}): WineListPdfStyle => ({
  ...defaultPdfStyle,
  ...style,
  margin: { ...defaultPdfStyle.margin, ...(style.margin || {}) },
  title: { ...defaultPdfStyle.title, ...(style.title || {}) },
  table: { ...defaultPdfStyle.table, ...(style.table || {}) },
  layout: { ...defaultLayout, ...(style.layout || {}) },
});

const firstLine = (lines: string[]): string => (lines.length > 0 ? lines[0] : '');

export const generateWineListPdf = async (
  wineList: Wine[],
  shopName?: string,
  style: Partial<WineListPdfStyle> = {}
): Promise<string> => {
  const pdfStyle = mergePdfStyle(style);
  const baseLayout = { ...pdfStyle.layout };

  if (!wineList || wineList.length === 0) {
    throw new Error('ワインリストが空です');
  }

  const imageOptions = baseLayout.centerCropImages
    ? { centerCrop: true as const }
    : {};

  if (pdfStyle.showImage) {
    await preloadWineImages(wineList, imageOptions);
  }

  const doc = new jsPDF(
    pdfStyle.orientation,
    'pt',
    pdfStyle.pageSize
  );

  const fontBase64 = await loadJapaneseFontBase64();
  const useJapaneseFont = registerJapaneseFont(doc, fontBase64);
  if (!useJapaneseFont) {
    throw new Error('フォントの登録に失敗しました');
  }

  const safeText = (text: string | null | undefined): string => {
    if (!text) return '';
    return String(text);
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableStartX = pdfStyle.margin.left;
  const tableWidth = pageWidth - pdfStyle.margin.left - pdfStyle.margin.right;

  const typeOrder: Wine['type'][] = ['スパークリング', 'ロゼ', '白', '赤'];
  const winesByType: Record<Wine['type'], Wine[]> = {
    赤: [],
    白: [],
    ロゼ: [],
    スパークリング: [],
  };

  wineList.forEach((wine) => {
    if (winesByType[wine.type]) {
      winesByType[wine.type].push(wine);
    }
  });

  (Object.keys(winesByType) as Wine['type'][]).forEach((type) => {
    winesByType[type].sort((a, b) => a.price - b.price);
  });

  const pageGroups: Wine['type'][][] =
    baseLayout.pageGroups && baseLayout.pageGroups.length > 0
      ? baseLayout.pageGroups
      : [typeOrder];

  const activePageGroups = pageGroups
    .map((types) => types.filter((type) => winesByType[type].length > 0))
    .filter((types) => types.length > 0);

  const formatUpdateDate = () =>
    new Date().toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const titleText = baseLayout.titleOverride
    ?? (shopName ? `${safeText(shopName)} - ワインリスト` : 'ワインリスト');

  const drawDocumentHeader = (
    y: number,
    layout: WineListPdfLayout,
    titleMarginBottom: number,
    titleFontSize: number = pdfStyle.title.fontSize
  ): number => {
    let nextY = y;

    if (layout.inlineHeader) {
      setPdfFont(doc, titleFontSize);
      doc.setTextColor(
        pdfStyle.title.color[0],
        pdfStyle.title.color[1],
        pdfStyle.title.color[2]
      );
      doc.text(titleText, tableStartX, nextY);

      if (layout.showDate) {
        setPdfFont(doc, Math.max(6, layout.dateFontSize * (titleFontSize / pdfStyle.title.fontSize)));
        doc.setTextColor(100, 100, 100);
        doc.text(
          `最終更新: ${formatUpdateDate()}`,
          pageWidth - pdfStyle.margin.right,
          nextY,
          { align: 'right' }
        );
      }

      nextY += titleFontSize + titleMarginBottom;
      return nextY;
    }

    setPdfFont(doc, titleFontSize);
    doc.setTextColor(
      pdfStyle.title.color[0],
      pdfStyle.title.color[1],
      pdfStyle.title.color[2]
    );
    doc.text(titleText, pageWidth / 2, nextY, { align: 'center' });
    nextY += titleFontSize + 2;

    if (layout.showShopAsSubtitle && shopName) {
      setPdfFont(doc, layout.subtitleFontSize);
      doc.setTextColor(100, 100, 100);
      doc.text(safeText(shopName), pageWidth / 2, nextY, { align: 'center' });
      nextY += layout.subtitleFontSize + 2;
    }

    nextY += titleMarginBottom;

    if (layout.showDate) {
      setPdfFont(doc, layout.dateFontSize);
      doc.setTextColor(100, 100, 100);
      doc.text(`最終更新: ${formatUpdateDate()}`, pageWidth / 2, nextY, { align: 'center' });
      nextY += layout.dateFontSize + 8;
    }

    return nextY;
  };

  activePageGroups.forEach((pageTypes, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage();
    }

    let layout = { ...baseLayout };
    let verticalDistribution: VerticalDistribution | null = null;

    if (layout.distributeVertically) {
      verticalDistribution = computeVerticalDistribution(
        pageHeight,
        pdfStyle,
        layout,
        winesByType,
        pageTypes,
        shopName
      );
      layout = {
        ...layout,
        rowMinHeight: verticalDistribution.uniformRowHeight,
        genreSectionSpacer: verticalDistribution.genreSectionSpacer,
        genreEndPadding: verticalDistribution.genreEndPadding,
        imageSize: verticalDistribution.imageSize,
        genreTitleFontSize: verticalDistribution.genreTitleFontSize,
        genreTitleHeight: verticalDistribution.genreTitleHeight,
        headerHeight: verticalDistribution.headerHeight,
        headerTextY: verticalDistribution.headerTextY,
        imageColWidth: verticalDistribution.imageColWidth,
        typeColWidth: verticalDistribution.typeColWidth,
        cellInset: verticalDistribution.cellInset,
      };
    }

    const tableFontSize = verticalDistribution?.tableFontSize ?? pdfStyle.table.fontSize;
    const titleFontSize = verticalDistribution?.titleFontSize ?? pdfStyle.title.fontSize;
    const metaFontSize = Math.max(5, tableFontSize - 1.2);

    const { headers, widths: colWidths } = getColumnLayout(
      tableWidth,
      pdfStyle.showImage,
      pdfStyle.showCharacteristics,
      layout.imageColWidth,
      layout.typeColWidth,
      layout.stackedDetail
    );

    const getColumnIndex = (
      name: 'image' | 'type' | 'detail' | 'name' | 'variety' | 'origin' | 'characteristics' | 'price'
    ) => {
      const order: string[] = [];
      if (pdfStyle.showImage) order.push('image');
      order.push('type');
      if (layout.stackedDetail) {
        order.push('detail');
      } else {
        order.push('name', 'variety', 'origin');
        if (pdfStyle.showCharacteristics) order.push('characteristics');
        order.push('price');
      }
      return order.indexOf(name);
    };

    const drawTableHeader = (y: number): number => {
      doc.setFillColor(
        pdfStyle.table.headerBackgroundColor[0],
        pdfStyle.table.headerBackgroundColor[1],
        pdfStyle.table.headerBackgroundColor[2]
      );
      doc.rect(tableStartX, y, tableWidth, layout.headerHeight, 'F');
      doc.setTextColor(
        pdfStyle.table.headerTextColor[0],
        pdfStyle.table.headerTextColor[1],
        pdfStyle.table.headerTextColor[2]
      );
      setPdfFont(doc, tableFontSize + 0.5);

      let headerX = tableStartX;
      headers.forEach((header, index) => {
        const isRight = header === '価格';
        doc.text(
          header,
          headerX + (isRight ? colWidths[index] - layout.cellInset / 2 : layout.cellInset / 2),
          y + layout.headerTextY,
          { align: isRight ? 'right' : 'left' }
        );
        // 2行詳細レイアウト: 「ワイン」列の右端に「価格」も表示
        if (layout.stackedDetail && header === 'ワイン') {
          doc.text(
            '価格',
            headerX + colWidths[index] - layout.cellInset / 2,
            y + layout.headerTextY,
            { align: 'right' }
          );
        }
        headerX += colWidths[index];
      });

      return y + layout.headerHeight;
    };

    let currentY =
      pdfStyle.margin.top + (verticalDistribution?.verticalTopOffset ?? 0);
    currentY = drawDocumentHeader(
      currentY,
      layout,
      verticalDistribution?.titleMarginBottom ?? pdfStyle.title.marginBottom,
      titleFontSize
    );

    let isFirstGenre = true;

    pageTypes.forEach((type) => {
      const wines = winesByType[type];
      if (wines.length === 0) return;

      if (!isFirstGenre) {
        currentY += layout.genreSectionSpacer;
      }
      isFirstGenre = false;

      setPdfFont(doc, layout.genreTitleFontSize);
      doc.setTextColor(
        pdfStyle.table.headerBackgroundColor[0],
        pdfStyle.table.headerBackgroundColor[1],
        pdfStyle.table.headerBackgroundColor[2]
      );
      doc.text(type, tableStartX, currentY);
      currentY += layout.genreTitleHeight;
      currentY = drawTableHeader(currentY);

      for (let index = 0; index < wines.length; index++) {
        const wine = wines[index];
        const bottlePrice = typeof wine.price === 'number' ? wine.price : 0;
        const rowHeight = layout.distributeVertically
          ? layout.rowMinHeight
          : Math.max(layout.rowMinHeight, layout.stackedDetail ? 36 : 28);

        if (index % 2 === 1) {
          doc.setFillColor(
            pdfStyle.table.alternateRowBackgroundColor[0],
            pdfStyle.table.alternateRowBackgroundColor[1],
            pdfStyle.table.alternateRowBackgroundColor[2]
          );
          doc.rect(tableStartX, currentY, tableWidth, rowHeight, 'F');
        }

        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(pdfStyle.table.lineWidth);
        doc.rect(tableStartX, currentY, tableWidth, rowHeight);

        let cellX = tableStartX;
        const typeCenterY = currentY + rowHeight / 2 + tableFontSize * 0.3;

        if (pdfStyle.showImage) {
          const imageDataUrl = wine.image
            ? getCachedImageDataUrl(wine.image, imageOptions)
            : null;
          if (imageDataUrl) {
            const imageX = cellX + (colWidths[0] - layout.imageSize) / 2;
            const imageY = currentY + (rowHeight - layout.imageSize) / 2;
            doc.addImage(
              imageDataUrl,
              getImageFormat(imageDataUrl),
              imageX,
              imageY,
              layout.imageSize,
              layout.imageSize
            );
          } else {
            setPdfFont(doc, tableFontSize);
            doc.setTextColor(
              pdfStyle.table.textColor[0],
              pdfStyle.table.textColor[1],
              pdfStyle.table.textColor[2]
            );
            doc.text('-', cellX + colWidths[0] / 2, typeCenterY, { align: 'center' });
          }
          cellX += colWidths[0];
        }

        setPdfFont(doc, tableFontSize);
        doc.setTextColor(
          pdfStyle.table.textColor[0],
          pdfStyle.table.textColor[1],
          pdfStyle.table.textColor[2]
        );
        doc.text(typeShortLabel[wine.type], cellX + 2, typeCenterY);
        cellX += colWidths[getColumnIndex('type')];

        if (layout.stackedDetail) {
          const detailIdx = getColumnIndex('detail');
          const detailW = colWidths[detailIdx];
          const detailLeft = cellX + layout.cellInset / 2;
          const detailRight = cellX + detailW - layout.cellInset / 2;
          const line1Y = currentY + rowHeight * 0.38;
          const line2Y = currentY + rowHeight * 0.72;

          let bottlePriceText: string;
          let glassPriceText = '';
          if (layout.glassPriceOnly) {
            bottlePriceText = wine.glassPrice
              ? `¥${wine.glassPrice.toLocaleString()}`
              : `¥${bottlePrice.toLocaleString()}`;
          } else {
            bottlePriceText = `¥${bottlePrice.toLocaleString()}`;
            if (pdfStyle.showGlassPrice && wine.isGlassWine && wine.glassPrice) {
              glassPriceText = `(G¥${wine.glassPrice.toLocaleString()})`;
            }
          }

          setPdfFont(doc, tableFontSize);
          doc.setTextColor(
            pdfStyle.table.textColor[0],
            pdfStyle.table.textColor[1],
            pdfStyle.table.textColor[2]
          );
          const priceWidth = doc.getTextWidth(bottlePriceText);
          const nameMaxWidth = Math.max(20, detailRight - detailLeft - priceWidth - 10);
          const nameText = truncateToWidth(doc, safeText(wine.name), nameMaxWidth);
          doc.text(nameText, detailLeft, line1Y);
          doc.text(bottlePriceText, detailRight, line1Y, { align: 'right' });

          setPdfFont(doc, metaFontSize);
          doc.setTextColor(META_TEXT_COLOR[0], META_TEXT_COLOR[1], META_TEXT_COLOR[2]);

          const varietyText = `品種：${safeText(wine.variety)}`;
          const originText = `産地：${safeText(wine.origin)}`;
          const glassWidth = glassPriceText ? doc.getTextWidth(glassPriceText) + 8 : 0;
          const originX = detailLeft + (detailRight - detailLeft) * 0.52;
          const varietyMaxWidth = Math.max(24, originX - detailLeft - 8);
          const varietyDisplay = truncateToWidth(doc, varietyText, varietyMaxWidth);
          const originMaxWidth = Math.max(
            24,
            detailRight - originX - glassWidth - 4
          );
          const originDisplay = truncateToWidth(doc, originText, originMaxWidth);

          doc.text(varietyDisplay, detailLeft + 4, line2Y);
          doc.text(originDisplay, originX, line2Y);
          if (glassPriceText) {
            doc.setTextColor(
              pdfStyle.table.textColor[0],
              pdfStyle.table.textColor[1],
              pdfStyle.table.textColor[2]
            );
            setPdfFont(doc, metaFontSize);
            doc.text(glassPriceText, detailRight, line2Y, { align: 'right' });
          }
        } else {
          const nameIdx = getColumnIndex('name');
          const varietyIdx = getColumnIndex('variety');
          const originIdx = getColumnIndex('origin');
          const characteristicsIdx = getColumnIndex('characteristics');
          const priceIdx = getColumnIndex('price');

          const nameLines = doc.splitTextToSize(
            safeText(wine.name),
            colWidths[nameIdx] - layout.cellInset
          );
          const varietyLines = doc.splitTextToSize(
            safeText(wine.variety),
            colWidths[varietyIdx] - layout.cellInset
          );
          const originLines = doc.splitTextToSize(
            safeText(wine.origin),
            colWidths[originIdx] - layout.cellInset
          );
          const characteristics = wine.characteristics
            ? safeText(
                Array.isArray(wine.characteristics)
                  ? wine.characteristics.join(', ')
                  : String(wine.characteristics)
              )
            : '';
          const charLines = pdfStyle.showCharacteristics
            ? doc.splitTextToSize(characteristics, colWidths[characteristicsIdx] - layout.cellInset)
            : [''];

          let priceText: string;
          if (layout.glassPriceOnly && wine.glassPrice) {
            priceText = `¥${wine.glassPrice.toLocaleString()}`;
          } else if (layout.glassPriceOnly) {
            priceText = `¥${bottlePrice.toLocaleString()}`;
          } else if (pdfStyle.showGlassPrice && wine.isGlassWine && wine.glassPrice) {
            priceText = `¥${bottlePrice.toLocaleString()} / G¥${wine.glassPrice.toLocaleString()}`;
          } else {
            priceText = `¥${bottlePrice.toLocaleString()}`;
          }

          const textY = layout.distributeVertically
            ? currentY + rowHeight / 2 + tableFontSize * 0.3
            : currentY + layout.cellTextY;

          if (layout.distributeVertically) {
            doc.text(firstLine(nameLines), cellX + 2, textY);
            cellX += colWidths[nameIdx];
            doc.text(firstLine(varietyLines), cellX + 2, textY);
            cellX += colWidths[varietyIdx];
            doc.text(firstLine(originLines), cellX + 2, textY);
            cellX += colWidths[originIdx];
            if (pdfStyle.showCharacteristics) {
              doc.text(firstLine(charLines), cellX + 2, textY);
              cellX += colWidths[characteristicsIdx];
            }
            doc.text(
              priceText,
              cellX + colWidths[priceIdx] - layout.cellInset / 2,
              textY,
              { align: 'right' }
            );
          } else {
            doc.text(nameLines, cellX + 2, textY);
            cellX += colWidths[nameIdx];
            doc.text(varietyLines, cellX + 2, textY);
            cellX += colWidths[varietyIdx];
            doc.text(originLines, cellX + 2, textY);
            cellX += colWidths[originIdx];
            if (pdfStyle.showCharacteristics) {
              doc.text(charLines, cellX + 2, textY);
              cellX += colWidths[characteristicsIdx];
            }
            doc.text(
              priceText,
              cellX + colWidths[priceIdx] - layout.cellInset / 2,
              textY,
              { align: 'right' }
            );
          }
        }

        currentY += rowHeight;
      }

      currentY += layout.genreEndPadding;
    });
  });

  if (baseLayout.showPageNumbers) {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      setPdfFont(doc, 9);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `ページ ${i} / ${pageCount}`,
        pageWidth / 2,
        pageHeight - 16,
        { align: 'center' }
      );
    }
  }

  const pdfBlob = doc.output('blob');
  return URL.createObjectURL(pdfBlob);
};
